import { useEffect, useState } from "react";
import AppConstantService from "../services/AppConstantService";

// Slot sizes (in hours) offered wherever a slot size is picked: one meal service, multi day
// chefit, coupon SKU create/edit and the coupon SKU picker. Overridable from the app constant
// below; this list is used when the constant is missing or unparseable.
export const SLOT_SIZE_OPTIONS_APP_CONSTANT = "SLOT_SIZE_OPTIONS_CENTAURUS";
export const DEFAULT_SLOT_SIZE_OPTIONS: number[] = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 10, 12];

const appConstantService = new AppConstantService();

// Accepts a JSON array ("[1, 1.5, 2]") or a comma separated list ("1, 1.5, 2").
// Returns null when the value holds no valid positive number.
export const parseSlotSizeOptions = (value: string | null): number[] | null => {
  if (!value) return null;
  let rawValues: unknown[];
  try {
    const parsed = JSON.parse(value);
    rawValues = Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    rawValues = value.split(",");
  }
  const options = Array.from(
    new Set(
      rawValues
        .map((rawValue) => Number(typeof rawValue === "string" ? rawValue.trim() : rawValue))
        .filter((option) => Number.isFinite(option) && option > 0)
    )
  ).sort((a, b) => a - b);
  return options.length > 0 ? options : null;
};

// Shared across screens so the constant is fetched once per page load; reset on failure so
// a later mount can retry.
let slotSizeOptionsRequest: Promise<number[]> | null = null;

const fetchSlotSizeOptions = (): Promise<number[]> => {
  if (!slotSizeOptionsRequest) {
    slotSizeOptionsRequest = appConstantService
      .fetchAppConstantValueByName(SLOT_SIZE_OPTIONS_APP_CONSTANT)
      .then((value) => parseSlotSizeOptions(value) ?? DEFAULT_SLOT_SIZE_OPTIONS)
      .catch(() => {
        slotSizeOptionsRequest = null;
        return DEFAULT_SLOT_SIZE_OPTIONS;
      });
  }
  return slotSizeOptionsRequest;
};

export const useSlotSizeOptions = (): number[] => {
  const [options, setOptions] = useState<number[]>(DEFAULT_SLOT_SIZE_OPTIONS);

  useEffect(() => {
    let isMounted = true;
    fetchSlotSizeOptions().then((fetchedOptions) => {
      if (isMounted) setOptions(fetchedOptions);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return options;
};
